import React, { useState } from 'react';
import styled from 'styled-components';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows } from '../../constants/theme';
import PrivilegedIdentityManagement from './PrivilegedIdentityManagement';
import { 
  mockAzureADUsers, 
  mockAzureADGroups, 
  mockConditionalAccessPolicies, 
  mockPrivilegedRoles,
  mockAzureADSyncStatus,
  mockPIMAlerts,
  mockAccessReviews,
  getUserLicensesSummary,
  getAuthMethodsSummary,
  getRiskSummary,
  getSyncErrorsBySeverity
} from '../../utils/mockAzureEntraData';
import { AzureADUser, AzureADGroup, ConditionalAccessPolicy } from '../../types/azureEntraId';

const Container = styled.div`
  padding: ${Spacing.xl}px;
  height: 100%;
  overflow-y: auto;
  background-color: ${Colors.background};
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.xl}px;
  padding: ${Spacing.lg}px;
  background: linear-gradient(135deg, #0078d4 0%, #106ebe 100%);
  border-radius: ${BorderRadius.lg}px;
  color: white;
`;

const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
`;

const MicrosoftLogo = styled.div`
  width: 48px;
  height: 48px;
  background: white;
  border-radius: ${BorderRadius.md}px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.bold};
  color: #0078d4;
`;

const HeaderTitle = styled.div`
  h1 {
    font-size: ${FontSizes.xxl}px;
    font-weight: ${FontWeights.bold};
    margin: 0 0 ${Spacing.xs}px 0;
  }
  
  p {
    margin: 0;
    opacity: 0.9;
    font-size: ${FontSizes.sm}px;
  }
`;

const SyncStatus = styled.div<{ status: 'success' | 'warning' | 'error' }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  background: ${props => {
    switch (props.status) {
      case 'success': return 'rgba(107, 203, 119, 0.2)';
      case 'warning': return 'rgba(255, 193, 7, 0.2)';
      case 'error': return 'rgba(220, 53, 69, 0.2)';
    }
  }};
  border-radius: ${BorderRadius.md}px;
  backdrop-filter: blur(10px);
`;

const SyncDot = styled.div<{ status: 'success' | 'warning' | 'error' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => {
    switch (props.status) {
      case 'success': return '#6bcb77';
      case 'warning': return '#ffc107';
      case 'error': return '#dc3545';
    }
  }};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${Spacing.lg}px;
  margin-bottom: ${Spacing.xl}px;
`;

const StatCard = styled.div`
  background: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.lg}px;
  box-shadow: ${Shadows.sm};
  border: 1px solid ${Colors.borderLight};
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${Shadows.md};
  }
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: ${Spacing.md}px;
`;

const StatIcon = styled.div<{ bgColor: string }>`
  width: 40px;
  height: 40px;
  border-radius: ${BorderRadius.md}px;
  background: ${props => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${FontSizes.lg}px;
  margin-right: ${Spacing.md}px;
`;

const StatTitle = styled.h3`
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textSecondary};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: ${FontSizes.hero}px;
  font-weight: ${FontWeights.bold};
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.xs}px;
`;

const StatDetail = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
`;

const TabNavigation = styled.div`
  display: flex;
  gap: ${Spacing.sm}px;
  margin-bottom: ${Spacing.xl}px;
  border-bottom: 2px solid ${Colors.borderLight};
  overflow-x: auto;
`;

const TabButton = styled.button<{ active: boolean }>`
  padding: ${Spacing.md}px ${Spacing.xl}px;
  background: none;
  border: none;
  border-bottom: 3px solid ${props => props.active ? '#0078d4' : 'transparent'};
  color: ${props => props.active ? '#0078d4' : Colors.textSecondary};
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 150ms ease;
  margin-bottom: -2px;
  white-space: nowrap;
  
  &:hover {
    color: #0078d4;
    background-color: ${Colors.surfaceHover};
  }
`;

const TabContent = styled.div`
  animation: fadeIn 300ms ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const TableContainer = styled.div`
  background: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.sm};
  overflow: hidden;
  margin-bottom: ${Spacing.xl}px;
`;

const TableHeader = styled.div`
  padding: ${Spacing.lg}px;
  border-bottom: 1px solid ${Colors.borderLight};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${Colors.backgroundDark};
`;

const TableTitle = styled.h2`
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: ${Spacing.sm}px ${Spacing.md}px;
  background: ${props => {
    switch (props.variant) {
      case 'primary': return '#0078d4';
      case 'danger': return Colors.danger;
      default: return Colors.surface;
    }
  }};
  color: ${props => props.variant ? Colors.textOnPrimary : Colors.textPrimary};
  border: 1px solid ${props => props.variant ? 'transparent' : Colors.border};
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 150ms ease;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: ${Colors.backgroundDark};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${Colors.borderLight};
  
  &:hover {
    background: ${Colors.surfaceHover};
  }
`;

const TableHeaderCell = styled.th`
  padding: ${Spacing.md}px;
  text-align: left;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableCell = styled.td`
  padding: ${Spacing.md}px;
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textPrimary};
`;

const StatusBadge = styled.span<{ 
  status: 'enabled' | 'disabled' | 'active' | 'inactive' | 'compliant' | 'noncompliant' | 'success' | 'warning' | 'error'
}>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: ${BorderRadius.round}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.semibold};
  background: ${props => {
    switch (props.status) {
      case 'enabled':
      case 'active':
      case 'compliant':
      case 'success': return Colors.success;
      case 'disabled':
      case 'inactive': return Colors.borderDark;
      case 'noncompliant':
      case 'error': return Colors.danger;
      case 'warning': return Colors.warning;
      default: return Colors.borderLight;
    }
  }};
  color: ${Colors.textOnPrimary};
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0078d4 0%, #106ebe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: ${FontWeights.semibold};
  font-size: ${FontSizes.sm}px;
  margin-right: ${Spacing.sm}px;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.div`
  font-weight: ${FontWeights.medium};
  color: ${Colors.textPrimary};
`;

const UserMeta = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
`;

const RiskIndicator = styled.div<{ level: 'none' | 'low' | 'medium' | 'high' }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => {
    switch (props.level) {
      case 'none': return Colors.success;
      case 'low': return Colors.info;
      case 'medium': return Colors.warning;
      case 'high': return Colors.danger;
    }
  }};
`;

const LicenseChip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  margin: 2px;
  background: #0078d4;
  color: white;
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.xs}px;
`;

const SearchBar = styled.input`
  padding: ${Spacing.sm}px ${Spacing.md}px;
  background: ${Colors.background};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textPrimary};
  min-width: 250px;
  
  &:focus {
    outline: none;
    border-color: #0078d4;
  }
`;

const AzureEntraIdDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'users' | 'groups' | 'policies' | 'pim' | 'governance' | 'sync'>('users');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredUsers = mockAzureADUsers.filter(user => 
    user.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.userPrincipalName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.department?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredGroups = mockAzureADGroups.filter(group =>
    group.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    group.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredPolicies = mockConditionalAccessPolicies.filter(policy =>
    policy.displayName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const licensesSummary = getUserLicensesSummary();
  const authMethodsSummary = getAuthMethodsSummary();
  const riskSummary = getRiskSummary();
  const syncErrorsSummary = getSyncErrorsBySeverity();

  const formatLastSignIn = (date?: Date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffHours < 48) return 'Yesterday';
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const getSyncStatusIcon = () => {
    if (mockAzureADSyncStatus.syncErrors.some(e => e.severity === 'Error' || e.severity === 'Critical')) {
      return 'error';
    }
    if (mockAzureADSyncStatus.syncErrors.some(e => e.severity === 'Warning')) {
      return 'warning';
    }
    return 'success';
  };

  return (
    <Container>
      <HeaderSection>
        <HeaderInfo>
          <MicrosoftLogo>M</MicrosoftLogo>
          <HeaderTitle>
            <h1>Azure Entra ID</h1>
            <p>Identity and Access Management • Tenant: cloudonmars.onmicrosoft.com</p>
          </HeaderTitle>
        </HeaderInfo>
        
        <SyncStatus status={getSyncStatusIcon()}>
          <SyncDot status={getSyncStatusIcon()} />
          <span>
            {mockAzureADSyncStatus.isDirSyncEnabled 
              ? `Last sync: ${formatLastSignIn(mockAzureADSyncStatus.lastSyncDateTime)}`
              : 'Cloud-only tenant'
            }
          </span>
        </SyncStatus>
      </HeaderSection>

      <StatsGrid>
        <StatCard>
          <StatHeader>
            <StatIcon bgColor="#0078d4">👥</StatIcon>
            <StatTitle>Total Users</StatTitle>
          </StatHeader>
          <StatValue>{mockAzureADUsers.length.toLocaleString()}</StatValue>
          <StatDetail>
            {mockAzureADUsers.filter(u => u.accountEnabled).length} active • {' '}
            {mockAzureADUsers.filter(u => !u.accountEnabled).length} disabled
          </StatDetail>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon bgColor="#107c10">🔐</StatIcon>
            <StatTitle>MFA Coverage</StatTitle>
          </StatHeader>
          <StatValue>
            {Math.round((mockAzureADUsers.filter(u => 
              u.authenticationMethods.some(m => m.type !== 'password')
            ).length / mockAzureADUsers.length) * 100)}%
          </StatValue>
          <StatDetail>
            {mockAzureADUsers.filter(u => 
              u.authenticationMethods.some(m => m.type !== 'password')
            ).length} users with strong auth
          </StatDetail>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon bgColor="#d83b01">⚠️</StatIcon>
            <StatTitle>Risk Users</StatTitle>
          </StatHeader>
          <StatValue>{riskSummary.medium + riskSummary.high}</StatValue>
          <StatDetail>
            {riskSummary.high} high risk • {riskSummary.medium} medium risk
          </StatDetail>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon bgColor="#8764b8">📦</StatIcon>
            <StatTitle>M365 Licenses</StatTitle>
          </StatHeader>
          <StatValue>
            {Object.values(licensesSummary).reduce((a, b) => a + b, 0)}
          </StatValue>
          <StatDetail>
            {Object.keys(licensesSummary).length} license types assigned
          </StatDetail>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon bgColor="#018281">🛡️</StatIcon>
            <StatTitle>CA Policies</StatTitle>
          </StatHeader>
          <StatValue>{mockConditionalAccessPolicies.length}</StatValue>
          <StatDetail>
            {mockConditionalAccessPolicies.filter(p => p.state === 'enabled').length} enabled • {' '}
            {mockConditionalAccessPolicies.filter(p => p.state === 'disabled').length} disabled
          </StatDetail>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon bgColor="#ca5010">🔑</StatIcon>
            <StatTitle>PIM Alerts</StatTitle>
          </StatHeader>
          <StatValue>{mockPIMAlerts.filter(a => a.isActive).length}</StatValue>
          <StatDetail>
            {mockPIMAlerts.filter(a => a.severity === 'High').length} high • {' '}
            {mockPIMAlerts.filter(a => a.severity === 'Medium').length} medium priority
          </StatDetail>
        </StatCard>
      </StatsGrid>

      <TabNavigation>
        <TabButton active={activeTab === 'users'} onClick={() => setActiveTab('users')}>
          Users & Groups
        </TabButton>
        <TabButton active={activeTab === 'groups'} onClick={() => setActiveTab('groups')}>
          Group Management
        </TabButton>
        <TabButton active={activeTab === 'policies'} onClick={() => setActiveTab('policies')}>
          Conditional Access
        </TabButton>
        <TabButton active={activeTab === 'pim'} onClick={() => setActiveTab('pim')}>
          Privileged Identity
        </TabButton>
        <TabButton active={activeTab === 'governance'} onClick={() => setActiveTab('governance')}>
          Identity Governance
        </TabButton>
        <TabButton active={activeTab === 'sync'} onClick={() => setActiveTab('sync')}>
          Directory Sync
        </TabButton>
      </TabNavigation>

      <TabContent>
        {activeTab === 'users' && (
          <TableContainer>
            <TableHeader>
              <TableTitle>Azure AD Users</TableTitle>
              <div style={{ display: 'flex', gap: `${Spacing.md}px`, alignItems: 'center' }}>
                <SearchBar
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <ActionButton variant="primary">+ Invite User</ActionButton>
                <ActionButton>Bulk Operations</ActionButton>
              </div>
            </TableHeader>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>User</TableHeaderCell>
                  <TableHeaderCell>Department</TableHeaderCell>
                  <TableHeaderCell>Account Status</TableHeaderCell>
                  <TableHeaderCell>Risk Level</TableHeaderCell>
                  <TableHeaderCell>MFA Status</TableHeaderCell>
                  <TableHeaderCell>Licenses</TableHeaderCell>
                  <TableHeaderCell>Last Sign-in</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableRow>
              </TableHead>
              <tbody>
                {filteredUsers.map(user => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <UserInfo>
                        <UserAvatar>
                          {user.givenName[0]}{user.surname[0]}
                        </UserAvatar>
                        <UserDetails>
                          <UserName>{user.displayName}</UserName>
                          <UserMeta>{user.userPrincipalName}</UserMeta>
                        </UserDetails>
                      </UserInfo>
                    </TableCell>
                    <TableCell>{user.department || 'Not set'}</TableCell>
                    <TableCell>
                      <StatusBadge status={user.accountEnabled ? 'enabled' : 'disabled'}>
                        {user.accountEnabled ? 'Enabled' : 'Disabled'}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: `${Spacing.xs}px` }}>
                        <RiskIndicator level={user.riskLevel} />
                        {user.riskLevel}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={
                        user.authenticationMethods.some(m => m.type !== 'password') ? 'success' : 'warning'
                      }>
                        {user.authenticationMethods.some(m => m.type !== 'password') ? 'Enabled' : 'Password Only'}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      <div>
                        {user.assignedLicenses.map(license => (
                          <LicenseChip key={license.id}>
                            {license.skuPartNumber}
                          </LicenseChip>
                        ))}
                        {user.assignedLicenses.length === 0 && <span style={{ color: Colors.textSecondary }}>None</span>}
                      </div>
                    </TableCell>
                    <TableCell>{formatLastSignIn(user.lastSignInDateTime)}</TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', gap: `${Spacing.xs}px` }}>
                        <ActionButton>Edit</ActionButton>
                        <ActionButton variant="danger">Reset Password</ActionButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        )}

        {activeTab === 'groups' && (
          <TableContainer>
            <TableHeader>
              <TableTitle>Azure AD Groups</TableTitle>
              <div style={{ display: 'flex', gap: `${Spacing.md}px`, alignItems: 'center' }}>
                <SearchBar
                  type="text"
                  placeholder="Search groups..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <ActionButton variant="primary">+ New Group</ActionButton>
              </div>
            </TableHeader>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Group Name</TableHeaderCell>
                  <TableHeaderCell>Type</TableHeaderCell>
                  <TableHeaderCell>Members</TableHeaderCell>
                  <TableHeaderCell>Classification</TableHeaderCell>
                  <TableHeaderCell>Membership</TableHeaderCell>
                  <TableHeaderCell>Sync Status</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableRow>
              </TableHead>
              <tbody>
                {filteredGroups.map(group => (
                  <TableRow key={group.id}>
                    <TableCell>
                      <div>
                        <div style={{ fontWeight: FontWeights.medium }}>{group.displayName}</div>
                        <div style={{ fontSize: FontSizes.xs, color: Colors.textSecondary }}>
                          {group.description || 'No description'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        {group.securityEnabled && <span>Security</span>}
                        {group.mailEnabled && <span>{group.securityEnabled ? ' • ' : ''}Mail-enabled</span>}
                        {group.teamEnabled && <span> • Teams</span>}
                      </div>
                    </TableCell>
                    <TableCell>{group.memberCount.toLocaleString()}</TableCell>
                    <TableCell>
                      <StatusBadge status={
                        group.classification === 'Restricted' ? 'error' :
                        group.classification === 'Confidential' ? 'warning' : 'success'
                      }>
                        {group.classification || 'Public'}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      {group.dynamicMembership ? (
                        <span>Dynamic: {group.membershipRuleProcessingState}</span>
                      ) : (
                        <span>Assigned</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={group.onPremisesSyncEnabled ? 'success' : 'inactive'}>
                        {group.onPremisesSyncEnabled ? 'Synced' : 'Cloud'}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', gap: `${Spacing.xs}px` }}>
                        <ActionButton>Members</ActionButton>
                        <ActionButton>Edit</ActionButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        )}

        {activeTab === 'policies' && (
          <TableContainer>
            <TableHeader>
              <TableTitle>Conditional Access Policies</TableTitle>
              <div style={{ display: 'flex', gap: `${Spacing.md}px`, alignItems: 'center' }}>
                <SearchBar
                  type="text"
                  placeholder="Search policies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <ActionButton variant="primary">+ New Policy</ActionButton>
                <ActionButton>Policy Templates</ActionButton>
              </div>
            </TableHeader>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Policy Name</TableHeaderCell>
                  <TableHeaderCell>State</TableHeaderCell>
                  <TableHeaderCell>Users</TableHeaderCell>
                  <TableHeaderCell>Applications</TableHeaderCell>
                  <TableHeaderCell>Grant Controls</TableHeaderCell>
                  <TableHeaderCell>Last Modified</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableRow>
              </TableHead>
              <tbody>
                {filteredPolicies.map(policy => (
                  <TableRow key={policy.id}>
                    <TableCell>
                      <div style={{ fontWeight: FontWeights.medium }}>{policy.displayName}</div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={
                        policy.state === 'enabled' ? 'enabled' :
                        policy.state === 'disabled' ? 'disabled' : 'warning'
                      }>
                        {policy.state.charAt(0).toUpperCase() + policy.state.slice(1)}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      {policy.conditions.users.includeUsers.includes('All') ? 'All users' :
                       `${policy.conditions.users.includeGroups.length} groups`}
                    </TableCell>
                    <TableCell>
                      {policy.conditions.applications.includeApplications.includes('All') ? 'All cloud apps' :
                       policy.conditions.applications.includeApplications.join(', ')}
                    </TableCell>
                    <TableCell>
                      {policy.grantControls?.builtInControls.join(', ') || 'Block'}
                    </TableCell>
                    <TableCell>{policy.modifiedDateTime.toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', gap: `${Spacing.xs}px` }}>
                        <ActionButton>Edit</ActionButton>
                        <ActionButton>Duplicate</ActionButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        )}

        {activeTab === 'pim' && (
          <PrivilegedIdentityManagement />
        )}

        {activeTab === 'governance' && (
          <TableContainer>
            <TableHeader>
              <TableTitle>Identity Governance & Access Reviews</TableTitle>
              <div style={{ display: 'flex', gap: `${Spacing.md}px`, alignItems: 'center' }}>
                <ActionButton variant="primary">+ Create Review</ActionButton>
                <ActionButton>Access Packages</ActionButton>
                <ActionButton>Terms of Use</ActionButton>
              </div>
            </TableHeader>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Review Name</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Scope</TableHeaderCell>
                  <TableHeaderCell>Reviewers</TableHeaderCell>
                  <TableHeaderCell>Progress</TableHeaderCell>
                  <TableHeaderCell>End Date</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableRow>
              </TableHead>
              <tbody>
                {mockAccessReviews.map(review => (
                  <TableRow key={review.id}>
                    <TableCell>
                      <div>
                        <div style={{ fontWeight: FontWeights.medium }}>{review.displayName}</div>
                        <div style={{ fontSize: FontSizes.xs, color: Colors.textSecondary }}>
                          {review.description}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={
                        review.status === 'InProgress' ? 'active' :
                        review.status === 'Completed' ? 'success' :
                        review.status === 'Scheduled' ? 'warning' : 'inactive'
                      }>
                        {review.status}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      External Guest Users
                    </TableCell>
                    <TableCell>
                      {review.reviewers.map(reviewer => reviewer.displayName).join(', ')}
                    </TableCell>
                    <TableCell>
                      {review.decisions.length} decisions made
                    </TableCell>
                    <TableCell>{review.endDateTime.toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', gap: `${Spacing.xs}px` }}>
                        <ActionButton>View Details</ActionButton>
                        <ActionButton>Export Results</ActionButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        )}

        {activeTab === 'sync' && (
          <div style={{ display: 'grid', gap: `${Spacing.xl}px` }}>
            <TableContainer>
              <TableHeader>
                <TableTitle>Azure AD Connect Sync Status</TableTitle>
                <div style={{ display: 'flex', gap: `${Spacing.md}px`, alignItems: 'center' }}>
                  <StatusBadge status={mockAzureADSyncStatus.status === 'Idle' ? 'success' : 'warning'}>
                    {mockAzureADSyncStatus.status}
                  </StatusBadge>
                  <ActionButton variant="primary">Force Sync</ActionButton>
                  <ActionButton>Configure Sync</ActionButton>
                </div>
              </TableHeader>
              <div style={{ padding: `${Spacing.lg}px` }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: `${Spacing.lg}px`, marginBottom: `${Spacing.xl}px` }}>
                  <div>
                    <div style={{ fontSize: `${FontSizes.sm}px`, color: Colors.textSecondary, marginBottom: `${Spacing.xs}px` }}>
                      Last Sync
                    </div>
                    <div style={{ fontSize: `${FontSizes.lg}px`, fontWeight: FontWeights.semibold }}>
                      {mockAzureADSyncStatus.lastSyncDateTime?.toLocaleString() || 'Never'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: `${FontSizes.sm}px`, color: Colors.textSecondary, marginBottom: `${Spacing.xs}px` }}>
                      Sync Interval
                    </div>
                    <div style={{ fontSize: `${FontSizes.lg}px`, fontWeight: FontWeights.semibold }}>
                      {mockAzureADSyncStatus.syncInterval} minutes
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: `${FontSizes.sm}px`, color: Colors.textSecondary, marginBottom: `${Spacing.xs}px` }}>
                      Pending Changes
                    </div>
                    <div style={{ fontSize: `${FontSizes.lg}px`, fontWeight: FontWeights.semibold }}>
                      {mockAzureADSyncStatus.pendingChanges}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: `${FontSizes.sm}px`, color: Colors.textSecondary, marginBottom: `${Spacing.xs}px` }}>
                      Sync Type
                    </div>
                    <div style={{ fontSize: `${FontSizes.lg}px`, fontWeight: FontWeights.semibold }}>
                      {mockAzureADSyncStatus.syncType}
                    </div>
                  </div>
                </div>
              </div>
            </TableContainer>

            {mockAzureADSyncStatus.syncErrors.length > 0 && (
              <TableContainer>
                <TableHeader>
                  <TableTitle>Sync Errors ({mockAzureADSyncStatus.syncErrors.length})</TableTitle>
                  <ActionButton>Download Error Report</ActionButton>
                </TableHeader>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Timestamp</TableHeaderCell>
                      <TableHeaderCell>Severity</TableHeaderCell>
                      <TableHeaderCell>Error Code</TableHeaderCell>
                      <TableHeaderCell>Affected Object</TableHeaderCell>
                      <TableHeaderCell>Error Message</TableHeaderCell>
                      <TableHeaderCell>Resolution</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <tbody>
                    {mockAzureADSyncStatus.syncErrors.map(error => (
                      <TableRow key={error.id}>
                        <TableCell>{error.timestamp.toLocaleString()}</TableCell>
                        <TableCell>
                          <StatusBadge status={
                            error.severity === 'Critical' || error.severity === 'Error' ? 'error' :
                            error.severity === 'Warning' ? 'warning' : 'success'
                          }>
                            {error.severity}
                          </StatusBadge>
                        </TableCell>
                        <TableCell style={{ fontFamily: 'monospace' }}>{error.errorCode}</TableCell>
                        <TableCell>{error.affectedObject}</TableCell>
                        <TableCell>{error.errorMessage}</TableCell>
                        <TableCell>{error.resolution || 'Contact support'}</TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </TableContainer>
            )}
          </div>
        )}
      </TabContent>
    </Container>
  );
};

export default AzureEntraIdDashboard;